import { ProductBadge } from '~/components';
import { VENDORS_ROUTER } from '~/constants/router';
import ShoppingCartButton from '~/domains/cart/ShoppingCartButton';
import DeliveryFlag from '~/domains/delivery/DeliveryFlag';
import PlusMembershipProductBadge from '~/domains/plusMembership/components/PlusMembershipProductBadge';
import { useTrackProductEventProps } from '~/hooks/event';
import { useLinkComponent } from '~/hooks/mobileApp';
import type { OAISchema } from '~/types/openapi';

import ProductImage from '../../ProductImage';
import ProductPrice from '../ProductPrice';
import ProductSalesTargetBadge from '../ProductSalesTargetBadge';

import styles from './index.module.css';

type Props = {
  inverse?: boolean;
  onClick?: (item: OAISchema['MarketSelectProductResponseV2']) => void;
  onCartClick?: (item: OAISchema['MarketSelectProductResponseV2']) => void; // TODO - 앰플리튜드 이벤트 한번 싹 정리할 떄, 제거 함.
  eventOption?: Record<string, unknown>;
  plusMembershipFullBadge?: boolean;
  reverseCouponDiscountedPrice?: boolean;
  useCartButton?: boolean;
  is3x2?: boolean;
  isAd?: boolean;
  isViewDeliveryFlag?: boolean;
} & OAISchema['MarketSelectProductResponseV2'];

const ProductCard = ({
  onClick,
  onCartClick,
  eventOption,
  plusMembershipFullBadge = false,
  reverseCouponDiscountedPrice = false,
  is3x2 = false,
  useCartButton = true,
  isViewDeliveryFlag = true,
  isAd = false,
  ...data
}: Props) => {
  const {
    offerId,
    vendorId,
    mainImages,
    productName,
    isOutOfStock,
    promotionText,
    status,
    inverse,
    mallName,
    deliveryFlags,
    isFreeShipping,
    salesTargets,
  } = data;
  const [props, { trackAddCart }] = useTrackProductEventProps(
    data,
    eventOption,
  );

  const LinkComponent = useLinkComponent();

  const handleCardClick = () => {
    props.onClick?.();
  };

  return (
    <LinkComponent href={VENDORS_ROUTER.productDetail(vendorId, offerId!)}>
      <div
        className="relative"
        role="presentation"
        {...props}
        onClick={handleCardClick}
      >
        {mainImages![0] && (
          <ProductImage
            width={100}
            height={100}
            sizes="45vw"
            layout="responsive"
            src={mainImages![0]}
            alt={productName}
            isOutOfStock={isOutOfStock}
            status={status}
            inverse={inverse}
            rightBottom={
              useCartButton ? (
                <ShoppingCartButton
                  className={styles.cartButton}
                  onClick={() => {
                    onClick?.(data);
                    trackAddCart?.();
                    onCartClick?.(data);
                  }}
                  item={data}
                />
              ) : null
            }
          />
        )}
        <ProductSalesTargetBadge
          salesTargets={salesTargets}
          className="mt-4"
          full={plusMembershipFullBadge}
        />
        {!is3x2 && <div className={styles.vendor}>{mallName}</div>}
        <div className={styles.title}>{productName}</div>
        {!is3x2 && promotionText && (
          <div className={styles.promotionText}>{promotionText}</div>
        )}
        <PlusMembershipProductBadge salesTargets={salesTargets} />
        <ProductPrice
          {...data}
          reverse={reverseCouponDiscountedPrice}
          is3x2={is3x2}
        />
        {isViewDeliveryFlag && (
          <>
            <DeliveryFlag flags={deliveryFlags} />
            {isFreeShipping && (
              <div className={styles.badges}>
                <ProductBadge type="freeDelivery" />
              </div>
            )}
          </>
        )}
        {isAd && (
          <p className="absolute bottom-0 right-0 text-gray-600 typo-footnote100-regular">
            AD
          </p>
        )}
      </div>
    </LinkComponent>
  );
};

export default ProductCard;
